import React from 'react';
import MarkdownRenderer from '../MarkdownRenderer';
import { getDecisionSummaryText } from '../../utils/candidateAnalysisUtils';
import './TabStyles.css';

const OverviewTab = ({ candidate }) => {
    const getSummary = () => getDecisionSummaryText(candidate) || 'No summary available.';

    // Get top strengths (max 3)
    const getStrengths = () => {
        if (candidate.rag_analysis?.evaluation_result) {
            const evalResult = candidate.rag_analysis.evaluation_result;
            // Try to get from dimension scores or summary
            if (evalResult.summary) {
                // Extract strengths from summary or use dimension scores
                const strengths = [];
                if (evalResult.skills_score?.strengths) {
                    strengths.push(...evalResult.skills_score.strengths);
                }
                if (evalResult.experience_score?.strengths) {
                    strengths.push(...evalResult.experience_score.strengths);
                }
                return strengths.slice(0, 3);
            }
        } else if (candidate.analysis?.overall_assessment?.strengths) {
            return candidate.analysis.overall_assessment.strengths.slice(0, 3);
        }
        return [];
    };

    // Get top risks (max 3)
    const getRisks = () => {
        if (candidate.rag_analysis?.evaluation_result) {
            const evalResult = candidate.rag_analysis.evaluation_result;
            const risks = [];
            if (evalResult.skills_score?.gaps) {
                risks.push(...evalResult.skills_score.gaps);
            }
            if (evalResult.experience_score?.gaps) {
                risks.push(...evalResult.experience_score.gaps);
            }
            if (evalResult.ineligibility_reasons) {
                risks.push(...evalResult.ineligibility_reasons);
            }
            return risks.slice(0, 3);
        } else if (candidate.analysis?.overall_assessment?.Shortfall_Areas) {
            return candidate.analysis.overall_assessment.Shortfall_Areas.slice(0, 3);
        }
        return [];
    };

    // Get hiring recommendation
    const getRecommendation = () => {
        const score = candidate.rag_analysis?.evaluation_result?.calibrated_score ||
                     candidate.rag_analysis?.evaluation_result?.fit_score ||
                     candidate.analysis?.fit_score ||
                     candidate.analysis?.score ||
                     0;
        
        const eligibility = candidate.rag_analysis?.evaluation_result?.eligibility_status;
        
        if (eligibility === 'ineligible') {
            return { label: 'No Hire', color: 'red', reason: 'Does not meet eligibility requirements' };
        } else if (eligibility === 'needs_review') {
            return { label: 'Hold', color: 'yellow', reason: 'Requires additional review' };
        } else if (score >= 75) {
            return { label: 'Hire', color: 'green', reason: 'Strong candidate with high fit score' };
        } else if (score >= 60) {
            return { label: 'Hold', color: 'yellow', reason: 'Moderate fit, consider for further evaluation' };
        } else {
            return { label: 'No Hire', color: 'red', reason: 'Low fit score' };
        }
    };

    const summary = getSummary();
    const strengths = getStrengths();
    const risks = getRisks();
    const recommendation = getRecommendation();

    return (
        <div className="tab-content overview-tab">
            <h2 className="section-header">DECISION SUMMARY</h2>
            
            {/* Unified assessment — scroll when long */}
            <div className="overview-section overview-summary-panel">
                <MarkdownRenderer text={summary} />
            </div>

            {/* Top Strengths */}
            {strengths.length > 0 && (
                <div className="overview-section">
                    <h3 className="section-header">KEY STRENGTHS</h3>
                    <ul className="strengths-list">
                        {strengths.map((strength, idx) => (
                            <li key={idx} className="strength-item">
                                <span className="strength-icon">✅</span>
                                <span>{strength}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Top Risks */}
            {risks.length > 0 && (
                <div className="overview-section">
                    <h3 className="section-header">KEY RISKS</h3>
                    <ul className="risks-list">
                        {risks.map((risk, idx) => (
                            <li key={idx} className="risk-item">
                                <span className="risk-icon">⚠️</span>
                                <span>{risk}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Hiring Recommendation */}
            <div className="overview-section recommendation-section">
                <h3 className="section-header">RECOMMENDATION</h3>
                <div className={`recommendation-badge recommendation-${recommendation.color}`}>
                    <span className="recommendation-label">{recommendation.label}</span>
                    <span className="recommendation-reason">{recommendation.reason}</span>
                </div>
            </div>
        </div>
    );
};

export default OverviewTab;
